import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import api, { apiError, API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { MagnifyingGlass, UploadSimple, UserPlus, PaperPlaneTilt, CheckCircle, Circle, Trash, SpinnerGap, Warning, EnvelopeSimple, ChatCircleText, CaretDown, DownloadSimple, Clock, ThumbsUp, XCircle, Prohibit, QrCode, WhatsappLogo, Copy, LinkSimple, PencilSimple, FileArrowUp, LockSimple } from "@phosphor-icons/react";
import GuideLink from "@/components/GuideLink";

function EditVoterDialog({ electionId, voter, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  useEffect(() => { if (voter) setF({ name: voter.name || "", email: voter.email || "", phone: voter.phone || "" }); }, [voter]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/elections/${electionId}/voters/${voter.id}`, f);
      toast.success(data.status === "unchanged" ? "No changes" : "Voter updated");
      onClose(); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!voter} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Edit voter</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label><Input data-testid="edit-voter-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label><Input data-testid="edit-voter-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Phone</Label><Input data-testid="edit-voter-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5" /></div>
          <p className="text-xs text-zinc-400">Changing email or phone is blocked on a live election once the voter has voted. Every contact change is recorded (values hashed) in the audit log.</p>
          <button data-testid="save-edit-voter" className="w-full h-11 bg-[#1F6FE5] text-white rounded-lg font-semibold hover:bg-[#1552C9] transition-colors active:scale-[0.98]">Save changes</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Add-voters tabs ---------- */

function AddManuallyTab({ electionId, published, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", station: "" });
  const [busy, setBusy] = useState(false);
  if (published) return <LockedNotice />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/elections/${electionId}/voters`, f);
      toast.success("Voter added");
      setF({ name: "", email: "", phone: "", station: "" }); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 items-end" data-testid="add-manually-form">
      <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label><Input data-testid="voter-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" placeholder="Ama Mensah" /></div>
      <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label><Input data-testid="voter-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" placeholder="ama@example.com" /></div>
      <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Phone</Label><Input data-testid="voter-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5" placeholder="0208…" /></div>
      <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Polling station <span className="text-zinc-300 normal-case">(optional)</span></Label><Input data-testid="voter-station" value={f.station} onChange={(e) => setF({ ...f, station: e.target.value })} className="mt-1.5" placeholder="e.g. North Hall" /></div>
      <div className="sm:col-span-2 flex items-center justify-between gap-3 pt-1">
        <p className="text-xs text-zinc-400">Email or phone is required. Station powers the live turnout-by-station board.</p>
        <button data-testid="save-voter" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1F6FE5] text-white rounded-lg font-semibold text-sm hover:bg-[#1552C9] transition-colors active:scale-95 disabled:opacity-60">
          {busy ? <SpinnerGap size={16} className="animate-spin" /> : <UserPlus size={16} />} Add voter
        </button>
      </div>
    </form>
  );
}

function LockedNotice() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-10" data-testid="voters-locked-notice">
      <LockSimple size={28} weight="duotone" className="text-slate-400 mb-2" />
      <p className="text-sm font-medium text-slate-600">The voter roll is locked</p>
      <p className="text-xs text-slate-400 mt-1 max-w-sm">This election has been published, so the roll can no longer be changed here. Use the tools below to resend invitations.</p>
    </div>
  );
}

function ImportCsvTab({ electionId, published, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef();
  if (published) return <LockedNotice />;

  const downloadTemplate = () => {
    const csv = "name,email,phone,station\nAma Mensah,ama@example.com,0208553679,North Hall\nKofi Boateng,kofi@example.com,,South Wing\nAkua Owusu,,0244123456,North Hall\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "selectflow_voters_template.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { toast.error("Please choose a .csv file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/elections/${electionId}/voters/validate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onInput = (e) => { handleFile(e.target.files[0]); e.target.value = ""; };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); };

  const doImport = async () => {
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => r.valid).map(({ name, email, phone, station }) => ({ name, email, phone, station }));
      const { data } = await api.post(`/elections/${electionId}/voters/import`, { rows });
      toast.success(`Imported ${data.imported} voters`);
      setPreview(null); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (preview) {
    return (
      <div data-testid="csv-preview">
        <div className="flex gap-4 text-sm mb-3">
          <span className="text-emerald-600 font-medium">{preview.summary.valid} valid</span>
          <span className="text-red-500 font-medium">{preview.summary.invalid} with errors</span>
        </div>
        <div className="max-h-72 overflow-auto border border-slate-200 rounded-xl">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Station</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {preview.rows.map((r, i) => (
                <TableRow key={i} data-testid={`preview-row-${i}`} className={r.valid ? "" : "bg-red-50/60"}>
                  <TableCell>{r.name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.email || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.phone || "—"}</TableCell>
                  <TableCell className="text-xs">{r.station || "—"}</TableCell>
                  <TableCell>{r.valid ? <span className="text-emerald-600 text-xs">Valid</span> : <span className="text-red-500 text-xs inline-flex items-center gap-1"><Warning size={13} /> {r.error}</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => setPreview(null)} className="px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium hover:border-slate-500 transition-colors">Back</button>
          <button data-testid="confirm-import" onClick={doImport} disabled={busy || preview.summary.valid === 0} className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1F6FE5] text-white rounded-lg text-sm font-semibold disabled:opacity-50 hover:bg-[#1552C9] transition-colors">
            {busy && <SpinnerGap size={16} className="animate-spin" />} Import {preview.summary.valid} voters
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <input ref={fileRef} data-testid="csv-input" type="file" accept=".csv" onChange={onInput} className="hidden" />
      <div
        role="button"
        tabIndex={0}
        data-testid="csv-upload-zone"
        onClick={() => fileRef.current.click()}
        onKeyDown={(e) => { if (e.key === "Enter") fileRef.current.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-12 text-center transition-colors ${drag ? "border-[#1F6FE5] bg-[#EAF1FD]" : "border-slate-300 bg-slate-50 hover:bg-[#EAF1FD]/50 hover:border-[#1F6FE5]"}`}
      >
        {busy
          ? <SpinnerGap size={30} className="animate-spin mx-auto text-[#1F6FE5]" />
          : <FileArrowUp size={34} className="mx-auto text-slate-400 mb-2" weight="duotone" />}
        <div className="font-medium text-slate-700 mt-2">Drag &amp; drop your CSV here, or click to browse</div>
        <div className="text-xs text-slate-400 mt-1 font-mono">COLUMNS: name, email, phone</div>
      </div>
      <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
        <p className="text-xs text-slate-400 max-w-md">Each row needs a name and an email or phone. Ghana numbers like 0208…​ are auto-converted to 233208…</p>
        <button data-testid="download-template-btn" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-sm text-[#1F6FE5] font-medium hover:underline">
          <DownloadSimple size={15} /> Download CSV template
        </button>
      </div>
    </div>
  );
}

function SelfRegistrationTab({ election }) {
  const regUrl = `${window.location.origin}/register/${election.id}`;
  const qrRef = useRef(null);
  const enabled = election.allow_self_registration;
  const copy = async () => { try { await navigator.clipboard.writeText(regUrl); toast.success("Registration link copied"); } catch { toast.error("Copy failed"); } };
  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png"); a.download = "self-registration-qr.png"; a.click();
    toast.success("QR downloaded");
  };
  return (
    <div className="flex flex-col sm:flex-row items-center gap-6" data-testid="self-registration-tab">
      <div ref={qrRef} className="bg-white p-3 rounded-xl border border-slate-200 shrink-0">
        <QRCodeCanvas value={regUrl} size={150} level="M" includeMargin fgColor="#0A0A0A" />
      </div>
      <div className="flex-1 min-w-0 w-full">
        {!enabled && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            Self-registration is currently <strong>off</strong>. Enable it in the <strong>Overview</strong> tab so this link accepts registrations.
          </p>
        )}
        <h4 className="font-display text-lg text-slate-900 mb-1">Let voters register themselves</h4>
        <p className="text-sm text-slate-500 mb-3">Share this link or QR code. Each person who registers lands in the roll below, awaiting your approval.</p>
        <div className="flex items-center gap-2">
          <Input readOnly value={regUrl} data-testid="self-reg-link" className="font-mono text-xs" />
          <button data-testid="self-reg-copy" onClick={copy} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:border-slate-500 transition-colors"><Copy size={15} /></button>
          <button data-testid="self-reg-download-qr" onClick={downloadQR} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:border-slate-500 transition-colors"><DownloadSimple size={15} /></button>
        </div>
      </div>
    </div>
  );
}

function AddVotersCard({ electionId, election, published, onDone }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" data-testid="add-voters-card">
      <h3 className="font-display text-xl text-slate-900 mb-4">Add voters</h3>
      <Tabs defaultValue="import">
        <TabsList className="bg-slate-100 rounded-lg p-1 gap-1 h-auto inline-flex">
          <TabsTrigger value="import" data-testid="voters-tab-import" className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#1F6FE5] data-[state=active]:shadow-sm text-slate-500 px-4 py-1.5 text-sm font-medium">
            <UploadSimple size={15} className="mr-1.5" /> Import CSV
          </TabsTrigger>
          <TabsTrigger value="manual" data-testid="voters-tab-manual" className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#1F6FE5] data-[state=active]:shadow-sm text-slate-500 px-4 py-1.5 text-sm font-medium">
            <UserPlus size={15} className="mr-1.5" /> Add Manually
          </TabsTrigger>
          <TabsTrigger value="self" data-testid="voters-tab-self" className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#1F6FE5] data-[state=active]:shadow-sm text-slate-500 px-4 py-1.5 text-sm font-medium">
            <QrCode size={15} className="mr-1.5" /> Self-Registration
          </TabsTrigger>
        </TabsList>
        <TabsContent value="import" className="pt-6"><ImportCsvTab electionId={electionId} published={published} onDone={onDone} /></TabsContent>
        <TabsContent value="manual" className="pt-6"><AddManuallyTab electionId={electionId} published={published} onDone={onDone} /></TabsContent>
        <TabsContent value="self" className="pt-6"><SelfRegistrationTab election={election} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Delivery + share ---------- */

function DeliveryBadges({ delivery, published }) {
  if (!published) return <span className="text-xs text-zinc-300">—</span>;
  if (!delivery || Object.keys(delivery).length === 0)
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-sm">Pending</span>;
  const chip = (ch, rec) => {
    const st = rec.status;
    const map = {
      delivered: "text-emerald-700 bg-emerald-50 border-emerald-200",
      sent: "text-blue-700 bg-blue-50 border-blue-200",
      issued: "text-violet-700 bg-violet-50 border-violet-200",
      failed: "text-red-600 bg-red-50 border-red-200",
      bounced: "text-red-600 bg-red-50 border-red-200",
    };
    const label = { delivered: "Delivered", sent: "Sent", issued: "Link ready", failed: "Failed", bounced: "Bounced" }[st] || st;
    const Icon = ch === "email" ? EnvelopeSimple : ch === "link" ? QrCode : ChatCircleText;
    return (
      <span key={ch} title={rec.message_id ? `${ch} · ${st} · ${rec.message_id}` : `${ch} · ${st}`}
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded-sm ${map[st] || map.sent}`}>
        <Icon size={11} weight="bold" /> {label}
      </span>
    );
  };
  return <div className="flex flex-wrap gap-1">{Object.entries(delivery).map(([ch, rec]) => chip(ch, rec))}</div>;
}

function ShareLinkDialog({ electionId, voter, onIssued }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const qrRef = useRef(null);

  const fetchLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/elections/${electionId}/voters/${voter.id}/link`);
      setData(data);
      onIssued && onIssued();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setOpen(false); }
    finally { setBusy(false); }
  };

  const onOpenChange = (o) => { setOpen(o); if (o) { setData(null); fetchLink(); } };

  const message = data
    ? `You're invited to vote in "${data.election_title}". Open your unique voting link (do not share it): ${data.link}${data.pin ? `\n\nNo smartphone? Dial ${data.dial_code} and enter PIN ${data.pin}.` : ""}`
    : "";

  const copyLink = async () => { try { await navigator.clipboard.writeText(data.link); toast.success("Link copied"); } catch { toast.error("Copy failed"); } };
  const copyMessage = async () => { try { await navigator.clipboard.writeText(message); toast.success("Message copied"); } catch { toast.error("Copy failed"); } };
  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `voting-qr-${(voter.name || voter.phone || voter.id).replace(/\s+/g, "_")}.png`;
    a.click();
    toast.success("QR downloaded");
  };
  const waHref = data ? `https://wa.me/${(voter.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(message)}` : "#";
  const smsHref = data ? `sms:${voter.phone || ""}?body=${encodeURIComponent(message)}` : "#";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button data-testid={`share-${voter.id}`} className="inline-flex items-center gap-1 text-zinc-400 hover:text-violet-600 transition-colors" title="Share voting link / QR">
          <QrCode size={16} weight="bold" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Share voting link</DialogTitle></DialogHeader>
        {busy || !data ? (
          <div className="py-12 text-center text-zinc-400"><SpinnerGap size={26} className="animate-spin mx-auto text-violet-500" /><p className="text-sm mt-3">Generating unique link…</p></div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-zinc-500">For <span className="font-medium text-zinc-700">{data.name || voter.phone || "this voter"}</span>. This is a single-use link — generating it again replaces any previous one.</p>
            <div ref={qrRef} className="flex justify-center bg-white p-4 border border-zinc-200 rounded-lg" data-testid="share-qr">
              <QRCodeCanvas value={data.link} size={200} level="M" includeMargin fgColor="#0A0A0A" />
            </div>
            <div className="flex items-center gap-2">
              <Input readOnly value={data.link} data-testid="share-link-input" className="font-mono text-xs" />
              <button data-testid="share-copy-link" onClick={copyLink} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors"><Copy size={15} /></button>
            </div>
            {data.pin && (
              <div className="text-xs bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 text-zinc-600">
                Feature phone: dial <span className="font-mono font-semibold">{data.dial_code}</span> and enter PIN <span className="font-mono font-semibold">{data.pin}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <a data-testid="share-whatsapp" href={waHref} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-[#25D366] text-white rounded-md text-sm font-semibold hover:brightness-95 transition-all active:scale-95">
                <WhatsappLogo size={17} weight="fill" /> WhatsApp
              </a>
              <a data-testid="share-sms" href={smsHref} className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-black text-white rounded-md text-sm font-semibold hover:bg-zinc-800 transition-colors active:scale-95">
                <ChatCircleText size={17} weight="fill" /> SMS
              </a>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="share-download-qr" onClick={downloadQR} className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors"><DownloadSimple size={16} /> Download QR</button>
              <button data-testid="share-copy-message" onClick={copyMessage} className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors"><LinkSimple size={16} /> Copy message</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDialog({ electionId, voters }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState([]);

  const eligible = voters.filter((v) => v.phone && !v.has_voted && v.status !== "pending" && v.status !== "rejected");
  const onOpenChange = (o) => { setOpen(o); if (o) { setIdx(0); setDone([]); } };

  const sendCurrent = async () => {
    const v = eligible[idx];
    if (!v) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/elections/${electionId}/voters/${v.id}/link`);
      const msg = `You're invited to vote in "${data.election_title}". Open your unique voting link (do not share it): ${data.link}${data.pin ? `\n\nNo smartphone? Dial ${data.dial_code} and enter PIN ${data.pin}.` : ""}`;
      window.open(`https://wa.me/${(v.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`, "_blank");
      setDone((d) => [...d, v.id]);
      setIdx((i) => i + 1);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const current = eligible[idx];
  const finished = idx >= eligible.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button data-testid="whatsapp-broadcast-btn" className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-[#25D366] text-white rounded-lg text-sm font-semibold hover:brightness-95 transition-all active:scale-95">
          <WhatsappLogo size={16} weight="fill" /> WhatsApp blast
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">WhatsApp blast</DialogTitle></DialogHeader>
        {eligible.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-500" data-testid="broadcast-empty">No pending voters with a phone number to message.</p>
        ) : finished ? (
          <div className="py-8 text-center" data-testid="broadcast-done">
            <CheckCircle size={40} weight="fill" className="text-emerald-500 mx-auto mb-3" />
            <p className="font-medium">Blast complete</p>
            <p className="text-sm text-zinc-500 mt-1">Opened WhatsApp for {done.length} of {eligible.length} voters.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">Voter <span className="font-mono">{idx + 1}</span> of <span className="font-mono">{eligible.length}</span></span>
              <span className="font-mono text-xs text-zinc-400">{done.length} sent</span>
            </div>
            <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full bg-[#25D366] rounded-full transition-all" style={{ width: `${(done.length / eligible.length) * 100}%` }} /></div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4" data-testid="broadcast-current">
              <div className="font-medium">{current.name || "Unnamed voter"}</div>
              <div className="text-sm text-zinc-500 font-mono">{current.phone}</div>
            </div>
            <p className="text-xs text-zinc-400">Each click mints a fresh single-use link and opens WhatsApp with the message ready. Send it, then continue to the next voter.</p>
            <div className="flex items-center gap-2">
              <button data-testid="broadcast-skip" onClick={() => setIdx((i) => i + 1)} className="px-3.5 py-2.5 border border-zinc-300 rounded-lg text-sm font-medium hover:border-black transition-colors">Skip</button>
              <button data-testid="broadcast-send" onClick={sendCurrent} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 px-3.5 py-2.5 bg-[#25D366] text-white rounded-lg text-sm font-semibold hover:brightness-95 transition-all active:scale-95 disabled:opacity-60">
                {busy ? <SpinnerGap size={16} className="animate-spin" /> : <WhatsappLogo size={16} weight="fill" />} Send &amp; next
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function VotersTab({ electionId, election, onChange }) {
  const [voters, setVoters] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [bulkChannel, setBulkChannel] = useState("auto");
  const [selected, setSelected] = useState(new Set());
  const [rejectVoter, setRejectVoter] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editVoter, setEditVoter] = useState(null);
  const { smsEnabled } = useAuth();
  const published = election.published;

  const load = useCallback(() => {
    api.get(`/elections/${electionId}/voters`, { params: { search, filter } }).then(({ data }) => setVoters(data));
  }, [electionId, search, filter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [filter, search]);

  const resend = async (id, channel = "auto") => {
    try { await api.post(`/elections/${electionId}/voters/${id}/resend`, null, { params: { channel } }); toast.success(`Invitation sent (${channel})`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resendAll = async () => {
    try { const { data } = await api.post(`/elections/${electionId}/resend-nonvoters`, null, { params: { channel: bulkChannel } }); toast.success(`Sent to ${data.count} non-voters via ${bulkChannel}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/voters/${id}`); toast.success("Removed"); load(); onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const approve = async (id) => {
    try { const { data } = await api.post(`/elections/${electionId}/voters/${id}/approve`); toast.success(data.invited ? "Approved — voting link sent" : "Approved"); load(); onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const approveBulk = async (ids) => {
    try {
      const { data } = await api.post(`/elections/${electionId}/voters/approve-bulk`, { voter_ids: ids });
      toast.success(`Approved ${data.approved}${data.invited ? ` · ${data.invited} link(s) sent` : ""}`);
      setSelected(new Set()); load(); onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doReject = async () => {
    try {
      const { data } = await api.post(`/elections/${electionId}/voters/${rejectVoter.id}/reject`, { reason: rejectReason });
      toast.success(data.notified ? "Rejected — registrant notified" : "Rejected");
      setRejectVoter(null); setRejectReason(""); load(); onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pendingIds = voters.filter((v) => v.status === "pending").map((v) => v.id);
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pendingIds));

  const exportRoll = async (fmt) => {
    try {
      const res = await fetch(`${API}/elections/${electionId}/voters/export?format=${fmt}`, { credentials: "include" });
      if (!res.ok) { toast.error("Export failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `voters_${electionId}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Voter roll exported (${fmt.toUpperCase()})`);
    } catch { toast.error("Export failed"); }
  };

  const [qrBusy, setQrBusy] = useState(false);
  const exportQrPdf = async () => {
    setQrBusy(true);
    try {
      const res = await fetch(`${API}/elections/${electionId}/voters/qr-pdf`, { method: "POST", credentials: "include" });
      if (!res.ok) {
        let msg = "QR export failed";
        try { msg = (await res.json()).detail || msg; } catch { /* ignore */ }
        toast.error(msg); return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `voter_qr_codes_${electionId}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Voting QR codes exported (PDF)");
    } catch { toast.error("QR export failed"); }
    finally { setQrBusy(false); }
  };

  return (
    <div className="space-y-5">
      {!election.demo_locked && <AddVotersCard electionId={electionId} election={election} published={published} onDone={() => { load(); onChange(); }} />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="voter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search voters…" className="pl-9" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="voter-filter" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All voters</SelectItem>
              <SelectItem value="voted">Voted</SelectItem>
              <SelectItem value="not_voted">Not voted</SelectItem>
              <SelectItem value="pending">Pending approval</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-voters-excel" onClick={() => exportRoll("xlsx")} className="inline-flex items-center gap-2 px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-medium hover:border-slate-500 transition-colors" title="Export roll as Excel"><DownloadSimple size={16} /> Excel</button>
          <button data-testid="export-voters-pdf" onClick={() => exportRoll("pdf")} className="inline-flex items-center gap-2 px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-medium hover:border-slate-500 transition-colors" title="Export roll as PDF"><DownloadSimple size={16} /> PDF</button>
          {published && (
            <button data-testid="export-qr-pdf" onClick={exportQrPdf} disabled={qrBusy} className="inline-flex items-center gap-2 px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-medium hover:border-[#1F6FE5] hover:text-[#1F6FE5] transition-colors disabled:opacity-60" title="Printable PDF of every un-voted voter's QR code">
              {qrBusy ? <SpinnerGap size={16} className="animate-spin" /> : <QrCode size={16} />} QR sheet
            </button>
          )}
          {published && (
            <div className="flex items-center gap-2">
              <Select value={bulkChannel} onValueChange={setBulkChannel}>
                <SelectTrigger data-testid="bulk-channel" className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms" disabled={!smsEnabled}>SMS{!smsEnabled ? " (off)" : ""}</SelectItem>
                  <SelectItem value="both" disabled={!smsEnabled}>Email + SMS</SelectItem>
                </SelectContent>
              </Select>
              <button data-testid="resend-all-btn" onClick={resendAll} className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-black text-white rounded-lg text-sm font-semibold hover:bg-zinc-800 transition-colors active:scale-95">
                <PaperPlaneTilt size={16} weight="fill" /> Resend to non-voters
              </button>
              <BroadcastDialog electionId={electionId} voters={voters} />
            </div>
          )}
          <GuideLink section="voter-roll" />
        </div>
      </div>

      {filter === "pending" && voters.length > 0 && (
        <div data-testid="bulk-approve-bar" className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <label className="flex items-center gap-2 text-sm font-medium text-amber-800 select-none cursor-pointer">
            <input type="checkbox" data-testid="select-all-pending" checked={allSelected} onChange={toggleAll} className="accent-[#1F6FE5]" /> Select all pending
          </label>
          <button data-testid="approve-selected-btn" onClick={() => approveBulk([...selected])} disabled={selected.size === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-40">
            <ThumbsUp size={15} weight="fill" /> Approve selected ({selected.size})
          </button>
          <button data-testid="approve-all-btn" onClick={() => approveBulk([])} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-600 text-emerald-700 rounded-lg text-sm font-semibold hover:bg-emerald-50 transition-colors">
            Approve all pending
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Invite</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {voters.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-zinc-400">No voters found.</TableCell></TableRow>
            ) : voters.map((v) => (
              <TableRow key={v.id} data-testid={`voter-row-${v.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2.5">
                    {v.status === "pending" && (
                      <input type="checkbox" data-testid={`select-${v.id}`} checked={selected.has(v.id)} onChange={() => toggleSel(v.id)} className="accent-[#1F6FE5]" />
                    )}
                    <span>{v.name || "—"}</span>
                    {v.station && (
                      <span data-testid={`station-${v.id}`} className="inline-flex items-center gap-1 text-[10px] font-medium text-[#1F6FE5] bg-[#EAF1FD] px-1.5 py-0.5 rounded">{v.station}</span>
                    )}
                    {v.attestation && (
                      <span data-testid={`attestation-${v.id}`} className="inline-flex gap-1 text-[10px] font-mono">
                        {["proposer", "seconder"].map((r) => {
                          const s = v.attestation[r]?.status;
                          const color = s === "attested" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : s === "declined" ? "text-red-700 bg-red-50 border-red-200" : s === "verified" ? "text-blue-700 bg-blue-50 border-blue-200" : "text-amber-700 bg-amber-50 border-amber-200";
                          return <span key={r} title={`${r}: ${v.attestation[r]?.reason || s}`} className={`border px-1 py-0.5 rounded-sm ${color}`}>{r[0].toUpperCase()}:{s}</span>;
                        })}
                      </span>
                    )}
                    {v.integrity_flag && (
                      <span data-testid={`integrity-flag-${v.id}`} title="Credential minted or contact edited within 30 min before this vote — review recommended" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-sm"><Warning size={11} weight="bold" /> Flagged</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs text-zinc-500">{v.email || v.phone}</TableCell>
                <TableCell data-testid={`delivery-${v.id}`}>{v.status === "pending" ? <span className="text-xs text-zinc-300">—</span> : <DeliveryBadges delivery={v.delivery} published={published} />}</TableCell>
                <TableCell>
                  {v.status === "payment_pending"
                    ? <span data-testid={`payment-pending-${v.id}`} className="inline-flex items-center gap-1.5 text-orange-500 text-xs font-medium"><Clock size={16} weight="fill" /> Awaiting payment</span>
                    : v.status === "pending"
                    ? <span className="inline-flex items-center gap-1.5 text-amber-600 text-xs font-medium"><Clock size={16} weight="fill" /> Pending</span>
                    : v.status === "rejected"
                    ? <span title={v.rejection_reason || ""} className="inline-flex items-center gap-1.5 text-red-500 text-xs font-medium"><Prohibit size={16} weight="fill" /> Rejected</span>
                    : v.has_voted
                    ? <span className="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-medium"><CheckCircle size={16} weight="fill" /> Voted</span>
                    : <span className="inline-flex items-center gap-1.5 text-zinc-400 text-xs"><Circle size={16} /> Not voted</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {v.status === "pending" && (
                      <>
                        <button data-testid={`approve-${v.id}`} onClick={() => approve(v.id)} className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 transition-colors text-xs font-semibold" title="Approve voter">
                          <ThumbsUp size={16} weight="fill" /> Approve
                        </button>
                        <button data-testid={`reject-${v.id}`} onClick={() => { setRejectVoter(v); setRejectReason(""); }} className="inline-flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors text-xs font-semibold" title="Reject voter">
                          <XCircle size={16} weight="fill" /> Reject
                        </button>
                      </>
                    )}
                    {published && !v.has_voted && v.status !== "pending" && v.status !== "rejected" && (
                      <ShareLinkDialog electionId={electionId} voter={v} onIssued={load} />
                    )}
                    {published && !v.has_voted && v.status !== "pending" && v.status !== "rejected" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`resend-${v.id}`} className="inline-flex items-center gap-1 text-zinc-400 hover:text-[#1F6FE5] transition-colors" title="Send invite">
                            <PaperPlaneTilt size={16} /><CaretDown size={11} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem data-testid={`resend-auto-${v.id}`} onClick={() => resend(v.id, "auto")}>Auto (best channel)</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`resend-email-${v.id}`} disabled={!v.email} onClick={() => resend(v.id, "email")}><EnvelopeSimple size={15} className="mr-2" /> Email</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`resend-sms-${v.id}`} disabled={!smsEnabled || !v.phone} onClick={() => resend(v.id, "sms")}><ChatCircleText size={15} className="mr-2" /> SMS {smsEnabled ? "" : "(off)"}</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {v.status !== "pending" && v.status !== "rejected" && (
                      <button data-testid={`edit-voter-${v.id}`} onClick={() => setEditVoter(v)} className="text-zinc-300 hover:text-[#1F6FE5] transition-colors" title="Edit voter"><PencilSimple size={16} /></button>
                    )}
                    {(!published || v.status === "pending" || v.status === "rejected") && <button data-testid={`del-voter-${v.id}`} onClick={() => del(v.id)} className="text-zinc-300 hover:text-red-500 transition-colors"><Trash size={16} /></button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!rejectVoter} onOpenChange={(o) => { if (!o) { setRejectVoter(null); setRejectReason(""); } }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Reject registration</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-500">Decline <span className="font-medium text-zinc-700">{rejectVoter?.name || rejectVoter?.email || rejectVoter?.phone}</span>. If they left contact details, we'll send them your note.</p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Reason (optional)</Label>
              <Textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="mt-1.5" placeholder="e.g. We couldn't verify your membership." />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => { setRejectVoter(null); setRejectReason(""); }} className="px-4 py-2.5 border border-zinc-300 rounded-lg text-sm font-medium">Cancel</button>
            <button data-testid="confirm-reject" onClick={doReject} className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition-colors">
              <XCircle size={16} weight="fill" /> Reject registration
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EditVoterDialog electionId={electionId} voter={editVoter} onClose={() => setEditVoter(null)} onDone={() => { load(); onChange(); }} />
    </div>
  );
}
